import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./style.module.css";
import "./animations.css";
import BlitzLogo from "../../../assets/images/blitz_logo.svg";
import TrackingMap from "./TrackingMap";
import Footer from "../../common/footerComponent";
import { searchAwb } from "../../../utils/api";
import {
  getAWBFailure,
  getAWBRequest,
  getAWBSuccess,
} from "../../../store/actions/awb";
import { errorCode, setEntrySource, timer, track } from "../../../analytics";
import usePageView from "../../../analytics/usePageView";

const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=919606097301";

const ERRORS = {
  invalid:
    "That doesn't look like a valid AWB number. Check the number in your order messages and try again.",
  failed: "We couldn't reach the tracking service. Please try again in a moment.",
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

const Search = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((store) => store.awbReducer?.loading);
  const [awb, setAwb] = useState("");
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(0);

  usePageView("home");
  const pasted = useRef(false); // tp_awb_search.source

  useEffect(() => {
    sessionStorage.clear("validateotp");
  }, []);

  const fail = (kind) => {
    setError(kind);
    setShake((s) => s + 1);
  };

  const handleTrack = async () => {
    const value = awb.trim();
    if (loading) return;
    const source = pasted.current ? "pasted" : "typed";
    if (!value) {
      track("tp_awb_search", { result: "invalid", reason: "empty", source });
      fail("invalid");
      return;
    }

    setError(null);
    dispatch(getAWBRequest());
    const elapsed = timer();
    const data = await searchAwb(value);

    if (data?.code === "200") {
      track("tp_awb_search", { result: "found", source, elapsed_ms: elapsed(), awb: value });
      setEntrySource("home_search");
      dispatch(getAWBSuccess("data", data));
      sessionStorage.setItem("awbNumber", JSON.stringify(value));
      navigate(`/${value}`);
    } else {
      const invalid = data?.code === "501";
      track("tp_awb_search", { result: invalid ? "invalid" : "error", source, code: errorCode(data), elapsed_ms: elapsed(), awb: value });
      dispatch(getAWBFailure());
      fail(invalid ? "invalid" : "failed");
    }
  };

  const hasValue = awb.trim().length > 0;

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={cx(styles.container, styles.headerInner)}>
          <img src={BlitzLogo} alt="blitz" className={styles.headerLogo} />
          <div className={styles.headerActions}>
            <span className={styles.helpText}>Need help?</span>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className={styles.chatBtn}
              onClick={() => track("tp_cta_click", { cta: "chat_support", placement: "header" })}
            >
              <span className={cx(styles.dot, styles.dotGreen)} />
              Chat with us
            </a>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <div className={cx(styles.container, styles.mainInner)}>
          <div className={styles.intro}>
            <div className={styles.copy}>
              <h1 className={styles.title}>
                Track your parcel, <span className={styles.titleAccent}>every step of the way.</span>
              </h1>
              <p className={styles.lead}>
                From pickup to your doorstep, see exactly where your Blitz shipment is and when it will arrive.
              </p>
            </div>

            <div className={styles.form}>
              <label htmlFor="awb" className={styles.label}>
                AWB number
              </label>
              <div
                className={cx(
                  styles.field,
                  focused && styles.fieldFocused,
                  error && styles.fieldError,
                  shake > 0 && (shake % 2 ? styles.shakeA : styles.shakeB)
                )}
              >
                <div className={styles.inputWrap}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cx(styles.inputIcon, (focused || hasValue) && styles.inputIconActive)}
                    aria-hidden="true"
                  >
                    <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
                    <path d="m3 8 9 5 9-5" />
                    <path d="M12 13v8" />
                  </svg>
                  <input
                    id="awb"
                    value={awb}
                    onChange={(e) => {
                      if (!e.target.value) pasted.current = false;
                      setAwb(e.target.value.toUpperCase());
                      setError(null);
                    }}
                    onPaste={() => {
                      pasted.current = true;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleTrack();
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    placeholder="Enter AWB number"
                    autoComplete="off"
                    spellCheck="false"
                    className={styles.input}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleTrack}
                  className={cx(styles.trackBtn, (hasValue || loading) && styles.trackBtnReady)}
                >
                  {loading ? (
                    <>
                      <span className={styles.spinner} />
                      Tracking
                    </>
                  ) : (
                    <>
                      Track
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 12h14" />
                        <path d="m13 6 6 6-6 6" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
              {error ? (
                <p className={styles.error} role="alert">
                  {ERRORS[error]}
                </p>
              ) : (
                <p className={styles.hint}>
                  Enter your AWB number to track the delivery status of your shipment. You can find it in messages
                  received for your order.
                </p>
              )}
            </div>
          </div>

          <div className={styles.visual}>
            <TrackingMap />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Search;
